import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Eye, EyeOff, RefreshCw, Copy, Download } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { formatDuration } from '@/utils/formatTime';
import LyricsPopoutDialog from './LyricsPopoutDialog';

interface SongwritingCollaborationProps {
  sessionName?: string;
}

const SongwritingCollaboration: React.FC<SongwritingCollaborationProps> = ({ 
  sessionName = 'Current Session'
}) => {
  const [privatelyrics, setPrivateLyrics] = useState('');
  const [sharedLyrics, setSharedLyrics] = useState('');
  const [collaborativeLyrics, setCollaborativeLyrics] = useState([
    {
      id: '1',
      author: 'Alex Turner',
      content: 'Verse 1:\nCity lights blur as we drive through the night\nMemories fade, but your voice stays bright',
      timestamp: new Date(Date.now() - 1000 * 60 * 5) // 5 minutes ago
    },
    {
      id: '2',
      author: 'Sam Wilson',
      content: 'Chorus:\nWe\'re chasing echoes of yesterday\nHolding onto moments that couldn\'t stay',
      timestamp: new Date(Date.now() - 1000 * 60 * 2) // 2 minutes ago
    }
  ]);
  const [showPrivateArea, setShowPrivateArea] = useState(true);
  
  const handleShareLyrics = () => {
    if (!privatelyrics.trim()) return;
    
    const newContribution = {
      id: Date.now().toString(),
      author: 'You (Jamie Collins)',
      content: privatelyrics,
      timestamp: new Date()
    };
    
    setCollaborativeLyrics([...collaborativeLyrics, newContribution]);
    setPrivateLyrics(''); // Clear private area after sharing
  };
  
  const togglePrivateArea = () => {
    setShowPrivateArea(!showPrivateArea);
  };
  
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-white/10 space-y-2">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-medium text-white">Songwriting Collaboration</h3>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={togglePrivateArea}
            className="h-8 px-2 text-xs gap-1 hover:bg-white/10"
          >
            {showPrivateArea ? (
              <>
                <EyeOff className="h-3.5 w-3.5" />
                Hide Private Area
              </>
            ) : (
              <>
                <Eye className="h-3.5 w-3.5" />
                Show Private Area
              </>
            )}
          </Button>
        </div>
        <p className="text-xs text-white/60">
          Work on lyrics for "{sessionName}" privately or collaborate with others.
        </p>
      </div>
      
      {showPrivateArea && (
        <div className="p-4 border-b border-white/10 space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="private-lyrics" className="text-sm font-medium text-purple-400">
              Your Private Space
            </Label>
            <div className="flex gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6" 
                onClick={() => setPrivateLyrics('')}
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6" 
                onClick={() => {
                  navigator.clipboard.writeText(privatelyrics);
                }}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-white/60 italic">
            This is your private writing space. No one else can see what you write here.
          </p>
          <div className="relative">
            <Textarea
              id="private-lyrics"
              value={privatelyrics}
              onChange={(e) => setPrivateLyrics(e.target.value)}
              placeholder="Write your lyrics here privately..."
              className="min-h-[120px] bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
            <LyricsPopoutDialog
              title="Your Private Lyrics"
              value={privatelyrics}
              onChange={setPrivateLyrics}
            />
          </div>
          <Button 
            onClick={handleShareLyrics} 
            variant="glass" 
            size="sm" 
            className="w-full mt-2"
          >
            <Send className="h-3.5 w-3.5 mr-2" />
            Share with Collaborators
          </Button>
        </div>
      )}
      
      <div className="p-4 border-b border-white/10 space-y-2">
        <Label htmlFor="shared-lyrics" className="text-sm font-medium text-blue-400">
          Collaborative Document
        </Label>
        <p className="text-xs text-white/60 italic">
          Everyone can see and edit this document in real-time.
        </p>
        <div className="relative">
          <Textarea
            id="shared-lyrics"
            value={sharedLyrics}
            onChange={(e) => setSharedLyrics(e.target.value)}
            placeholder="Collaborate on lyrics together here..."
            className="min-h-[100px] bg-white/5 border-white/10 text-white placeholder:text-white/30"
          />
          <LyricsPopoutDialog
            title="Collaborative Lyrics"
            value={sharedLyrics}
            onChange={setSharedLyrics}
          />
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-medium text-white">Shared Contributions</h3>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 px-2 text-xs"
            onClick={() => {
              const allLyrics = collaborativeLyrics
                .map(item => `${item.author} (${formatTime(item.timestamp)}):\n${item.content}`)
                .join('\n\n');
              navigator.clipboard.writeText(allLyrics);
            }}
          >
            <Copy className="h-3.5 w-3.5 mr-1" />
            Copy All
          </Button>
        </div>
        
        {collaborativeLyrics.map((item) => (
          <div key={item.id} className="p-3 rounded-md bg-white/5 space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-white">{item.author}</span>
              <span className="text-xs text-white/50">{formatTime(item.timestamp)}</span>
            </div>
            <div className="whitespace-pre-line text-sm text-white/80">
              {item.content}
            </div>
            <div className="flex justify-end pt-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 px-2 text-xs"
                onClick={() => {
                  navigator.clipboard.writeText(item.content);
                }}
              >
                <Copy className="h-3 w-3 mr-1" />
                Copy
              </Button>
            </div>
          </div>
        ))}
      </div>
      
      <div className="p-4 border-t border-white/10">
        <Button 
          variant="neon" 
          size="sm" 
          className="w-full gap-1"
          onClick={() => {
            const allLyrics = collaborativeLyrics
              .map(item => `${item.author} (${formatTime(item.timestamp)}):\n${item.content}`)
              .join('\n\n');
            
            const blob = new Blob([allLyrics], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `lyrics_${sessionName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }}
        >
          <Download className="h-4 w-4" />
          Export Lyrics
        </Button>
      </div>
    </div>
  );
};

export default SongwritingCollaboration;
